import {
  ErrorRounded as ShowUnmappedTIVIcon,
  ChevronRightRounded as HideUnmappedTIVIcon,
} from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import IconButton from "components/common/IconButton";
import Visible from "components/common/Visible";
import L from "leaflet";
import HeatmapOverlay from "leaflet-heatmap";
import "leaflet/dist/leaflet.css";
import { useState } from "react";
import { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import * as staticDataService from "services/staticDataService";

const HeatmapLayer = ({ data, options }) => {
  const map = useMap();

  useEffect(() => {
    const heatmapLayer = new HeatmapOverlay(options);

    heatmapLayer.setData(data);
    heatmapLayer.addTo(map);
    map.attributionControl.addAttribution(
      '<a href="http://www.geonames.org/">GeoNames</a>'
    );
    map.attributionControl.addAttribution(
      '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    );

    return () => {
      map.removeLayer(heatmapLayer);
    };
  }, [map, data, options]);

  return null;
};

const MissingTivWarning = ({ missingTiv }) => {
  const map = useMap();
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    const control = L.control({ position: "topright" });

    const container = L.DomUtil.create("div");

    const root = ReactDOM.createRoot(container);

    control.onAdd = () => {
      root.render(
        <div
          style={{
            padding: "2px 6px",
            background: "red",
            borderRadius: "8px",
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"flex-start"}
            spacing={0.5}
          >
            {isExpanded && (
              <Typography
                paddingLeft={1}
                sx={{ fontWeight: "bold", color: "white !important" }}
              >
                {`$${(missingTiv ?? 0).toLocaleString(
                  "en-US"
                )} TIV failed to geolocate`}
              </Typography>
            )}
            <IconButton
              sx={{ color: "white !important" }}
              icon={isExpanded ? HideUnmappedTIVIcon : ShowUnmappedTIVIcon}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </Stack>
        </div>
      );
      return container;
    };

    control.addTo(map);

    return () => {
      map.removeControl(control);
    };
  }, [map, isExpanded, missingTiv]);

  return null;
};

const SovHeatmap = ({ sov, loading, height }) => {
  const { data: sovConfig } = staticDataService.useConfigQuery("sov");
  const valueColumn = sovConfig?.geoHeatmapValueColumn ?? "_TIV";

  const missingTiv = (sov?.locations ?? [])
    .filter(
      (location) => !(location.latitude != null && location.longitude != null)
    )
    .map((location) => location[valueColumn])
    .reduce((acc, value) => acc + value, 0);

  const heatmapOptions = {
    radius: 20,
    maxOpacity: 0.8,
    scaleRadius: false,
    useLocalExtrema: true,
    latField: "lat",
    lngField: "lng",
    valueField: "value",
  };

  const heatmapData = {
    data: (sov?.locations || [])
      .map((location) => {
        if (location.latitude && location.longitude) {
          return {
            lat: location.latitude,
            lng: location.longitude,
            value: location[valueColumn],
          };
        }
        return null;
      })
      .filter((plot) => plot),
  };

  return (
    <MapContainer
      center={[40, -100]}
      zoom={4}
      style={{
        height: height ?? "40vh",
        width: "100%",
        borderRadius: "0.625rem",
      }}
    >
      <TileLayer url={"https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"} />
      <HeatmapLayer data={heatmapData} options={heatmapOptions} />
      <Visible byTag={"input.sov.heatmap.warning"} defaultVisible>
        {!loading && missingTiv > 0 && (
          <MissingTivWarning missingTiv={missingTiv} />
        )}
      </Visible>
    </MapContainer>
  );
};

export default SovHeatmap;
